<template>
  <template v-if="isVisible">
    <Modal
      :show="true"
      :title="t('Alterar Senha')"
      :saveLabel="isSubmitting ? t('Alterando...') : t('Salvar')"
      :saveFn="handleSubmit"
      :saveDisabledFn="() => !isFormValid || isSubmitting || passwordMismatch"
      :closeLabel="t('Cancelar')"
      size="small"
      @close="handleClose"
      @dismiss="handleClose"
    >
    <form data-testid="users-change-password-form" @submit.prevent="handleSubmit">
      <div class="form-group">
        <div class="input-group">
          <span class="input-group-addon" style="white-space:nowrap;">
            {{ t('Usuário') }}
          </span>
          <div class="form-control-static">{{ user.name || user.username }}</div>
        </div>
      </div>

      <input-text
        v-model="formData.password"
        elem-id="changepw_password"
        type="password"
        :label="t('Nova Senha')"
        :placeholder="t('Nova Senha')"
        :required="true"
        :validation="() => formData.password.length >= 8"
        autocomplete="new-password"
        data-testid="users-change-password-new"
      />
      <small class="form-text text-muted" style="margin-top: -10px; margin-bottom: 10px; display: block;">
        {{ t('Mínimo 8 caracteres') }}
      </small>

      <input-text
        v-model="formData.confirmPassword"
        elem-id="confirm-password"
        type="password"
        :label="t('Confirmar Senha')"
        :placeholder="t('Confirmar Senha')"
        :required="true"
        :validation="() => formData.confirmPassword.length >= 8 && !passwordMismatch"
        autocomplete="new-password"
        data-testid="users-change-password-confirm"
      />

      <div v-if="passwordMismatch && formData.confirmPassword" class="alert alert-danger">
        <i class="pi pi-times-circle"></i> {{ t('As senhas não coincidem') }}
      </div>

      <div v-if="isFormValid" class="alert alert-success">
        <i class="pi pi-check-circle"></i> {{ t('Senha válida') }}
      </div>
    </form>
    </Modal>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUsers } from '@/composables/useUsers';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

interface User {
  id?: string | number;
  sub?: string;
  username?: string;
  name?: string;
  [key: string]: unknown;
}

interface OpenPayload {
  user?: User;
  [key: string]: unknown;
}

const { t } = useI18n();

// Composables
const { changePassword } = useUsers();

// State
const isVisible = ref<boolean>(false);
const user = ref<User>({});
const formData = ref<{ password: string; confirmPassword: string }>({
  password: '',
  confirmPassword: ''
});

const isSubmitting = ref<boolean>(false);

// Computed
const passwordMismatch = computed<boolean>(() => {
  return formData.value.password.length > 0 &&
         formData.value.confirmPassword.length > 0 &&
         formData.value.password !== formData.value.confirmPassword;
});

const isFormValid = computed<boolean>(() => {
  return formData.value.password.length >= 8 &&
         formData.value.confirmPassword.length >= 8 &&
         formData.value.password === formData.value.confirmPassword;
});

// Methods
const resetForm = (): void => {
  formData.value = {
    password: '',
    confirmPassword: ''
  };
  isSubmitting.value = false;
};

const handleClose = (): void => {
  log('ChangePasswordModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  user.value = {};
  resetForm();
};

const handleSubmit = async (): Promise<void> => {
  if (!isFormValid.value) {
    eventBus.emit('globalMessage.error', t('Por favor, preencha todos os campos obrigatórios.'));
    return;
  }

  if (passwordMismatch.value) {
    eventBus.emit('globalMessage.error', t('As senhas não coincidem'));
    return;
  }

  log('ChangePasswordModal', 'handleSubmit', 'Submitting password change', {
    username: user.value.username
  });

  isSubmitting.value = true;

  try {
    await changePassword(user.value.username as string, formData.value.password);
    eventBus.emit('globalMessage.success', t('Senha alterada com sucesso'));
    log('ChangePasswordModal', 'handleSubmit', 'Password changed successfully');
    eventBus.emit('user.password-changed', { userId: user.value.id });
    handleClose();
  } catch (error: unknown) {
    log('ChangePasswordModal', 'handleSubmit', 'Error changing password', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao alterar senha') + ': ' + message);
  } finally {
    isSubmitting.value = false;
  }
};

// EventBus handlers
const handleOpen = (data: OpenPayload | User | undefined): void => {
  log('ChangePasswordModal', 'handleOpen', 'Opening modal via eventBus', data);
  const payload = data as OpenPayload;
  user.value = (payload?.user || data || {}) as User;
  resetForm();
  isVisible.value = true;
};

// Lifecycle
onMounted(() => {
  log('ChangePasswordModal', 'onMounted', 'Modal mounted, listening for events');
  eventBus.on('modal.changepassword.open', handleOpen);
});

onBeforeUnmount(() => {
  log('ChangePasswordModal', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('modal.changepassword.open', handleOpen);
});
</script>

<style scoped>
.form-group {
  margin-bottom: 15px;
}

.text-danger {
  color: #dd4b39;
}

.text-muted {
  color: #777;
}

.form-control-static {
  padding-top: 7px;
  padding-bottom: 7px;
  margin-bottom: 0;
  min-height: 34px;
  font-weight: bold;
}

.alert {
  padding: 10px 15px;
  margin-top: 15px;
  border: 1px solid transparent;
  border-radius: 4px;
}

.alert-danger {
  color: #a94442;
  background-color: #f2dede;
  border-color: #ebccd1;
}
</style>
